import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navItems, services, companyInfo } from '../data';

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isProductsPage = location.pathname.startsWith('/produk-layanan') ||
                         location.pathname === '/pendanaan-bisnis' ||
                         location.pathname === '/produk-layanan/market-marketing-research' ||
                         location.pathname === '/produk-layanan/finance-tax-accounting' ||
                         location.pathname === '/produk-layanan/business-proposal-pitch-deck' ||
                         location.pathname === '/produk-layanan/business-advisory' ||
                         location.pathname === '/produk-layanan/business-it-service' ||
                         location.pathname === '/produk-layanan/business-strategy-audit' ||
                         location.pathname === '/produk-layanan/human-resource-planning-training' ||
                         location.pathname === '/produk-layanan/industry-economic-analysis';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Only track sections on homepage
      if (!isProductsPage && location.pathname === '/') {
        const sections = ['home', 'about', 'funding', 'team', 'testimonials', 'contact'];
        for (const section of sections.reverse()) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProductsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [location.pathname, isProductsPage]);

  const scrollToSection = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
    setIsProductsDropdownOpen(false);
  };

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();

    if (href.startsWith('#')) {
      // Internal link
      if (isProductsPage) {
        // On products page, navigate to home then scroll
        navigate('/', { replace: true });
        setTimeout(() => scrollToSection(href), 100);
      } else {
        scrollToSection(href);
      }
    }
    setIsProductsDropdownOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 py-3 transition-all duration-500 ${
          isScrolled || isProductsPage
            ? 'bg-dark-900/80 backdrop-blur-md border-b border-white/10'
            : 'bg-dark-900/80 backdrop-blur-md border-b border-white/10'
        }`}
      >
        <div className="max-w-7xl mx-auto section-padding">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                to="/"
                className="flex items-center gap-3 group"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                }}
              >
                <div className="relative w-48 h-14 flex items-center">
                  <img
                    src="/emp-logo-header.svg"
                    alt="Emporium Solution"
                    className="w-full h-full object-contain"
                  />
                </div>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item, index) => {
                const isHashLink = item.href.startsWith('#');
                const isProductsLink = item.label === 'Produk & Layanan';
                const isActive = isHashLink
                  ? !isProductsPage && activeSection === item.href.replace('#', '')
                  : isProductsPage && location.pathname === item.href;

                if (isProductsLink) {
                  return (
                    <motion.div
                      key={item.href}
                      ref={dropdownRef}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                      className="relative"
                    >
                      <button
                        onClick={() => setIsProductsDropdownOpen(!isProductsDropdownOpen)}
                        onMouseEnter={() => setIsProductsDropdownOpen(true)}
                        className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full flex items-center gap-1 ${
                          isProductsPage
                            ? 'text-primary-400'
                            : 'text-white/70 hover:text-white'
                        }`}
                      >
                        {isProductsPage && (
                          <motion.div
                            layoutId="activeNav"
                            className="absolute inset-0 bg-white/10 rounded-full"
                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                          />
                        )}
                        <span className="relative z-10">{item.label}</span>
                        <motion.span
                          animate={{ rotate: isProductsDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="relative z-10"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </motion.span>
                      </button>

                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {isProductsDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-80 bg-dark-800/95 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl overflow-hidden"
                          >
                            <div className="p-2">
                              {/* All Services */}
                              {services.map((service, idx) => {
                                // Define custom routes for specific services
                                let serviceRoute = `/produk-layanan/${service.id}`;
                                if (idx === 0) serviceRoute = '/produk-layanan';
                                if (service.id === 2) serviceRoute = '/produk-layanan/market-marketing-research';
                                if (service.id === 3) serviceRoute = '/produk-layanan/finance-tax-accounting';
                                if (service.id === 4) serviceRoute = '/produk-layanan/business-proposal-pitch-deck';
                                if (service.id === 5) serviceRoute = '/produk-layanan/business-advisory';
                                if (service.id === 6) serviceRoute = '/produk-layanan/business-it-service';
                                if (service.id === 7) serviceRoute = '/produk-layanan/business-strategy-audit';
                                if (service.id === 8) serviceRoute = '/produk-layanan/human-resource-planning-training';
                                if (service.id === 9) serviceRoute = '/produk-layanan/industry-economic-analysis';
                                if (service.id === 10) serviceRoute = '/pendanaan-bisnis';
                                
                                const isServiceActive = location.pathname === serviceRoute;
                                
                                return (
                                  <Link
                                    key={service.id}
                                    to={serviceRoute}
                                    onClick={() => setIsProductsDropdownOpen(false)}
                                    className={`block w-full px-4 py-3 text-left text-sm rounded-lg transition-colors whitespace-nowrap ${
                                      isServiceActive
                                        ? 'bg-primary-600 text-white'
                                        : 'text-white hover:bg-white/10'
                                    }`}
                                  >
                                    {service.title}
                                  </Link>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                }

                return isHashLink ? (
                  <motion.a
                    key={item.href}
                    href={item.href}
                    onClick={(e) => handleNavClick(e, item.href)}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full ${
                      isActive
                        ? 'text-primary-400'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activeNav"
                        className="absolute inset-0 bg-white/10 rounded-full"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{item.label}</span>
                  </motion.a>
                ) : (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index, duration: 0.5 }}
                  >
                    <Link
                      to={item.href}
                      className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 rounded-full inline-block ${
                        isActive
                          ? 'text-primary-400'
                          : 'text-white/70 hover:text-white'
                      }`}
                    >
                      {isActive && (
                        <motion.div
                          layoutId="activeNav"
                          className="absolute inset-0 bg-white/10 rounded-full"
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        />
                      )}
                      <span className="relative z-10">{item.label}</span>
                    </Link>
                  </motion.div>
                );
              })}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <motion.a
                href={`https://wa.me/${companyInfo.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-semibold rounded-full hover:bg-primary-700 transition-colors duration-300 shadow-lg shadow-primary-500/30"
              >
                <Phone className="w-4 h-4" />
                Hubungi Kami
              </motion.a>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white/70 hover:text-white transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden glass border-t border-white/20 overflow-hidden"
            >
              <nav className="flex flex-col p-4 gap-2">
                {navItems.map((item, index) => {
                  const isHashLink = item.href.startsWith('#');
                  const isProductsLink = item.label === 'Produk & Layanan';
                  const isActive = isHashLink
                    ? !isProductsPage && activeSection === item.href.replace('#', '')
                    : isProductsPage && location.pathname === item.href;

                  if (isProductsLink) {
                    return (
                      <motion.div
                        key={item.href}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 * index }}
                        className="border-t border-white/10 pt-2 mt-2"
                      >
                        <p className="px-4 py-2 text-xs text-white/50 font-medium uppercase tracking-wider">
                          {item.label}
                        </p>
                        {services.map((service, idx) => {
                          // Define custom routes for specific services (mobile)
                          let serviceRoute = `/produk-layanan/${service.id}`;
                          if (idx === 0) serviceRoute = '/produk-layanan';
                          if (service.id === 2) serviceRoute = '/produk-layanan/market-marketing-research';
                          if (service.id === 3) serviceRoute = '/produk-layanan/finance-tax-accounting';
                          if (service.id === 4) serviceRoute = '/produk-layanan/business-proposal-pitch-deck';
                          if (service.id === 5) serviceRoute = '/produk-layanan/business-advisory';
                          if (service.id === 6) serviceRoute = '/produk-layanan/business-it-service';
                          if (service.id === 7) serviceRoute = '/produk-layanan/business-strategy-audit';
                          if (service.id === 8) serviceRoute = '/produk-layanan/human-resource-planning-training';
                          if (service.id === 9) serviceRoute = '/produk-layanan/industry-economic-analysis';
                          if (service.id === 10) serviceRoute = '/pendanaan-bisnis';
                          
                          const isServiceActive = location.pathname === serviceRoute;
                          
                          return (
                            <Link
                              key={service.id}
                              to={serviceRoute}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block w-full px-4 py-3 rounded-xl font-medium transition-colors ${
                                isServiceActive
                                  ? 'bg-primary-600/20 text-primary-400'
                                  : 'text-white/70 hover:bg-white/10'
                              }`}
                            >
                              {service.title}
                            </Link>
                          );
                        })}
                      </motion.div>
                    );
                  }

                  return isHashLink ? (
                    <motion.a
                      key={item.href}
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                      className={`px-4 py-3 rounded-xl font-medium transition-colors ${
                        isActive
                          ? 'bg-white/10 text-primary-400'
                          : 'text-white/70 hover:bg-white/10'
                      }`}
                    >
                      {item.label}
                    </motion.a>
                  ) : (
                    <motion.div
                      key={item.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.05 * index }}
                    >
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 rounded-xl font-medium transition-colors ${
                          isActive
                            ? 'bg-white/10 text-primary-400'
                            : 'text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  );
                })}
                
                <motion.a
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center justify-center gap-2 px-4 py-3 mt-2 bg-primary-600 text-white font-semibold rounded-xl"
                >
                  <Phone className="w-4 h-4" />
                  Hubungi Kami
                </motion.a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Spacer for fixed header */}
      <div className="h-16" />
    </>
  );
}
